import { useEffect, useSyncExternalStore } from "react";
import { getAuthHeaders } from "../utils/authHeaders";
import {
    type GameEnabledRow,
    readPlayerVisibility,
    resolvePlayerGameState,
} from "../utils/gamePlayerVisibility";

const GAMES_ENABLED_REVISION_POLL_MS = 2 * 60 * 1000; // revision check every 2 min while tab is open
const LEGACY_GAMES_ENABLED_STORAGE_KEYS = ["c8_games_enabled_v1", "c8_games_enabled_v2"] as const;

type GamesEnabledSnapshot = {
    data: GameEnabledRow[];
    loading: boolean;
    error: boolean;
};

let gamesEnabledFetchPromise: Promise<{ data: GameEnabledRow[]; revision: number | null }> | null = null;
const gamesEnabledListeners = new Set<() => void>();
let revisionWatchStarted = false;
let knownRevision: number | null = null;

let snapshot: GamesEnabledSnapshot = { data: [], loading: true, error: false };

function emitGamesEnabledChange() {
    gamesEnabledListeners.forEach((listener) => listener());
}

function subscribeGamesEnabled(listener: () => void) {
    gamesEnabledListeners.add(listener);
    return () => {
        gamesEnabledListeners.delete(listener);
    };
}

function getGamesEnabledSnapshot(): GamesEnabledSnapshot {
    return snapshot;
}

function updateGamesEnabledSnapshot(partial: Partial<GamesEnabledSnapshot>) {
    snapshot = { ...snapshot, ...partial };
    emitGamesEnabledChange();
}

function clearLegacyGamesEnabledStorage() {
    if (typeof window === "undefined") return;
    for (const key of LEGACY_GAMES_ENABLED_STORAGE_KEYS) {
        try {
            sessionStorage.removeItem(key);
        } catch {
            // ignore
        }
    }
}

export function invalidateGamesEnabledCache() {
    gamesEnabledFetchPromise = null;
    knownRevision = null;
    clearLegacyGamesEnabledStorage();
    updateGamesEnabledSnapshot({ data: [], loading: true, error: false });
    void ensureGamesEnabledLoaded(true);
}

function parseGamesList(parsed: unknown): GameEnabledRow[] {
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object" && "data" in parsed && Array.isArray((parsed as { data: unknown }).data)) {
        return (parsed as { data: GameEnabledRow[] }).data;
    }
    return [];
}

function parseApiResponse(parsed: unknown): { data: GameEnabledRow[]; revision: number | null } {
    if (parsed && typeof parsed === "object" && "data" in parsed) {
        const payload = parsed as { data?: unknown; revision?: unknown };
        return {
            data: parseGamesList(parsed),
            revision: typeof payload.revision === "number" ? payload.revision : null,
        };
    }
    return { data: parseGamesList(parsed), revision: null };
}

function fetchGamesEnabledFromApi(force = false): Promise<{ data: GameEnabledRow[]; revision: number | null }> {
    if (!gamesEnabledFetchPromise) {
        const url = new URL(`${process.env.API_URL}/isgameenabled/ALL`);
        if (force) url.searchParams.set("_", String(Date.now()));

        gamesEnabledFetchPromise = fetch(url.toString(), {
            method: "GET",
            headers: getAuthHeaders(),
            cache: "no-store",
        })
            .then((res) => {
                if (res.status !== 200) return Promise.reject(new Error(`HTTP ${res.status}`));
                return res.json();
            })
            .then((parsed) => {
                const { data, revision } = parseApiResponse(parsed);
                knownRevision = revision;
                return { data, revision };
            })
            .finally(() => {
                gamesEnabledFetchPromise = null;
            });
    }
    return gamesEnabledFetchPromise;
}

function fetchRemoteGamesRevision(): Promise<number | null> {
    const url = new URL(`${process.env.API_URL}/player-games/revision`);
    url.searchParams.set("_", String(Date.now()));

    return fetch(url.toString(), {
        method: "GET",
        headers: getAuthHeaders(),
        cache: "no-store",
    })
        .then((res) => {
            if (res.status !== 200) return Promise.reject(new Error(`HTTP ${res.status}`));
            return res.json();
        })
        .then((parsed) => {
            if (parsed && typeof parsed === "object" && typeof (parsed as { revision?: unknown }).revision === "number") {
                return (parsed as { revision: number }).revision;
            }
            return null;
        });
}

function checkRemoteGamesRevision(): Promise<void> {
    return fetchRemoteGamesRevision()
        .then((remoteRevision) => {
            if (remoteRevision == null) return;
            if (knownRevision != null && remoteRevision === knownRevision) return;
            return ensureGamesEnabledLoaded(true);
        })
        .catch(() => {});
}

function startGamesEnabledRevisionWatch() {
    if (typeof window === "undefined" || revisionWatchStarted) return;
    revisionWatchStarted = true;

    const onVisibilityChange = () => {
        if (document.hidden) return;
        void checkRemoteGamesRevision();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.setInterval(() => {
        if (!document.hidden) void checkRemoteGamesRevision();
    }, GAMES_ENABLED_REVISION_POLL_MS);

    void checkRemoteGamesRevision();
}

/**
 * Start loading game visibility as early as possible (e.g. from index.tsx before React mounts).
 */
export function prefetchGamesEnabled(): void {
    if (typeof window === "undefined") return;
    clearLegacyGamesEnabledStorage();
    void ensureGamesEnabledLoaded(false);
    startGamesEnabledRevisionWatch();
}

function ensureGamesEnabledLoaded(force = false): Promise<void> {
    const hasDisplayData = snapshot.data.length > 0;
    if (!hasDisplayData) {
        updateGamesEnabledSnapshot({ loading: true, error: false });
    }

    return fetchGamesEnabledFromApi(force)
        .then(({ data }) => {
            updateGamesEnabledSnapshot({ data, loading: false, error: false });
        })
        .catch(() => {
            if (!hasDisplayData) {
                updateGamesEnabledSnapshot({ data: [], loading: false, error: true });
            } else {
                updateGamesEnabledSnapshot({ loading: false, error: true });
            }
        });
}

function deriveFlags(data: GameEnabledRow[], isLoading: boolean) {
    const byName = (name: string) => data.find((x) => (x.game_name || "").toLowerCase() === name.toLowerCase());
    const resolveGame = (name: string) => {
        const row = byName(name);
        if (isLoading && !row) {
            return { visible: false, live: false, comingSoon: false };
        }
        return resolvePlayerGameState(row);
    };
    const bullion = resolveGame("Bullion");
    const sixes = resolveGame("Sixes");
    const squares = resolveGame("Squares");
    const trader = resolveGame("Trader");
    const quickfire = resolveGame("Quickfire");
    return {
        isBullionEnabled: bullion.live,
        isBullionVisible: bullion.visible,
        isBullionComingSoon: bullion.comingSoon,
        isSixesEnabled: sixes.live,
        isSixesVisible: sixes.visible,
        isSixesComingSoon: sixes.comingSoon,
        isSquaresEnabled: squares.live,
        isSquaresVisible: squares.visible,
        isSquaresComingSoon: squares.comingSoon,
        isTraderEnabled: trader.live,
        isTraderVisible: trader.visible,
        isTraderComingSoon: trader.comingSoon,
        isQuickfireEnabled: quickfire.live,
        isQuickfireVisible: quickfire.visible,
        isQuickfireComingSoon: quickfire.comingSoon,
        bullionVisibility: readPlayerVisibility(byName("Bullion")),
        sixesVisibility: readPlayerVisibility(byName("Sixes")),
        squaresVisibility: readPlayerVisibility(byName("Squares")),
        traderVisibility: readPlayerVisibility(byName("Trader")),
        quickfireVisibility: readPlayerVisibility(byName("Quickfire")),
    };
}

export interface UseGamesEnabledResult {
    data: GameEnabledRow[];
    isTraderEnabled: boolean;
    isTraderVisible: boolean;
    isTraderComingSoon: boolean;
    isSquaresEnabled: boolean;
    isSquaresVisible: boolean;
    isSquaresComingSoon: boolean;
    isQuickfireEnabled: boolean;
    isQuickfireVisible: boolean;
    isQuickfireComingSoon: boolean;
    isBullionEnabled: boolean;
    isBullionVisible: boolean;
    isBullionComingSoon: boolean;
    isSixesEnabled: boolean;
    isSixesVisible: boolean;
    isSixesComingSoon: boolean;
    loading: boolean;
    error: boolean;
}

/**
 * Shared hook for /isgameenabled/ALL. Loads once on mount; polls a tiny revision endpoint
 * every 2 minutes while the tab is visible (plus when the tab is refocused) and refetches
 */
export function useGamesEnabled(): UseGamesEnabledResult {
    const { data, loading, error } = useSyncExternalStore(
        subscribeGamesEnabled,
        getGamesEnabledSnapshot,
        getGamesEnabledSnapshot,
    );

    useEffect(() => {
        void ensureGamesEnabledLoaded(false);
        startGamesEnabledRevisionWatch();
    }, []);

    const flags = deriveFlags(data, loading);
    return {
        data,
        ...flags,
        loading,
        error,
    };
}
